import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import Info from '@mui/icons-material/Info';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import AddIcon from '@mui/icons-material/Add';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import PrintIcon from '@mui/icons-material/Print';
import { Box } from '@mui/material';
import ReactDOM from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useLocation, Link } from 'react-router-dom';

function InvoiceIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');
    const location = useLocation();

    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReload') === 'true';

        // Only reload data if navigated from PurchaseOrderCreate and flag is set
        if (location.state?.from === '/InvoiceCreate' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else if (location.state?.from === '/InvoiceEdit' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else if (location.state?.from === '/InvoiceDetails' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else {
            // Populate data without reloading for other cases
            setFromDate(getFormattedStartDate());
            setToDate(getFormattedCurrentDate());
        }
    }, [location]);

    useEffect(() => {
        // // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }
        // Token is not expired, fetch data

        fetchTableData();
    }, [isAuthenticated, companyId, branchId, fromDate, toDate]);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    //search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    // Function to fetch table data and set state
    const fetchTableData = async () => {
        setData([]);
        setLoading(true);
        fetchData('InvoiceApi', `/INvoice/${companyId}/${fromDate}/${toDate}`, token)//, token
            .then((response) => {
                if (response.responseCode == '1111') {
                    console.log(response);
                    setData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Invoice Details:', error);
            })
            .finally(() => {
                setLoading(false); // Hide loading screen
            });
    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const handleDelete = async () => {
        deleteData('InvoiceApi', `${deleteItemId}`, token)//, token
            .then((response) => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('InvoiceApi', `${itemId}`)//, token
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }
   


    const generateInvoice = async (saleId) => {
        try {
            // ===== FETCH DATA =====
            const response = await fetchData('InvoiceApi', `/SalePrint/${saleId}`, token);
            const labelData = response.response_data;

            if (!labelData?.SalePrint || !labelData?.SalePrintItems) {
                throw new Error("Invalid invoice data.");
            }

            const header = labelData.SalePrint;
            const items = labelData.SalePrintItems;

            // ===== INITIALIZE PDF =====
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            let pageWidth = doc.internal.pageSize.getWidth();
            const boxX = 5;
            const boxWidth = pageWidth - 10;
            const rowHeight = 7;
            const midX = pageWidth - 65;

            // ===== TOP SECTION =====
            doc.setFontSize(18).setFont("times", "bold");

            // TAX INVOICE (centered)
            doc.text("TAX INVOICE", pageWidth / 2, 50, { align: "center" });

            // VAT Number (right aligned)
            doc.setFontSize(11).setFont("times", "bold");
            doc.text(`Our VATIN : ${header.CompanyVATNumber || ''}`, pageWidth - 7, 50, { align: "right" });

            // move y start below this
            let y = 56;

            // ===== HEADER SECTION =====
            const labelX = boxX + 2;
            const valueX = boxX + 28;
            const invoiceLabelX = midX + 2;
            const invoiceValueX = midX + 23;

            const baseRowHeight = 8; // compact row height
            const consigneeLineHeight = 5; // tighter line height

            // Split consignee text into lines
            const consigneeMaxWidth = midX - valueX - 5;
            const consigneeName = header.ConsigneeName || '';
            const consigneeLines = doc.splitTextToSize(consigneeName, consigneeMaxWidth);
            const consigneeHeight = consigneeLines.length * consigneeLineHeight;

            // Dynamic row height for consignee/date row
            const row1Height = baseRowHeight; // Customer
            const row2Height = baseRowHeight; // VAT / Invoice
            const row3Height = Math.max(consigneeHeight + 2, baseRowHeight); // Consignee / Date
            const row4Height = baseRowHeight; // BL Number
            const totalBoxHeight = row1Height + row2Height + row3Height + row4Height;

            // ===== Draw Outer Border =====
            doc.setLineWidth(0.2);
            doc.rect(boxX, y, boxWidth, totalBoxHeight);

            // ===== Horizontal Lines =====
            let currentY = y;
            doc.line(boxX, currentY + row1Height, boxX + boxWidth, currentY + row1Height);               // after Customer
            doc.line(boxX, currentY + row1Height + row2Height, boxX + boxWidth, currentY + row1Height + row2Height); // after VAT/Invoice
            doc.line(boxX, currentY + row1Height + row2Height + row3Height, boxX + boxWidth, currentY + row1Height + row2Height + row3Height); // after Consignee/Date

            // ===== Vertical Divider =====
            doc.line(midX, y + row1Height, midX, y + row1Height + row2Height + row3Height); // full height across VAT+Consignee sections

            // ===== Row 1: Customer =====
            doc.setFontSize(11).setFont("times", "normal");
            doc.text("Customer :", boxX + 2, y + 5);
            doc.setFont("times", "bold");
            doc.text(header.CustomerName || '', boxX + 28, y + 5);

            // ===== Row 2: VAT / Invoice =====
            doc.setFontSize(11).setFont("times", "normal");
            doc.text("VAT Number :", boxX + 2, y + row1Height + 5);
            doc.setFont("times", "bold");
            doc.text(header.CustomerVatNumber || '', boxX + 28, y + row1Height + 5);

            doc.setFontSize(11).setFont("times", "normal");
            doc.text("Invoice No :", invoiceLabelX, y + row1Height + 5);
            doc.setFont("times", "bold");
            doc.text(header.BillNumber || '', invoiceValueX, y + row1Height + 5);

            // ===== Row 3: Consignee / Date =====
            const consigneeY = y + row1Height + row2Height + 5;
            doc.setFontSize(11).setFont("times", "normal");
            doc.text("Consignee :", labelX, consigneeY);
            doc.setFont("times", "bold");
            doc.text(consigneeLines, valueX, consigneeY);

            doc.setFontSize(11).setFont("times", "normal");
            doc.text("Date :", invoiceLabelX, consigneeY);
            doc.setFont("times", "bold");
            doc.text(
                header.BillDate ? new Date(header.BillDate).toLocaleDateString('en-GB') : '',
                invoiceValueX,
                consigneeY
            );

            // ===== Row 4: BL Number =====
            const blY = y + row1Height + row2Height + row3Height + 5;
            doc.setFontSize(11).setFont("times", "normal");
            doc.text("BL Number :", boxX + 2, blY);
            doc.setFont("times", "bold");
            doc.text(header.BLNumber || '', boxX + 28, blY);

            // ===== Update Y (Start of Table Section) =====
            y = y + totalBoxHeight;


            const taxableAmount = items.reduce((sum, i) => sum + (i.Amount ?? 0), 0);
            const totalAmount = items.reduce((sum, i) => sum + (i.TotalAmount ?? 0), 0);
            const totalVat = items.reduce((sum, i) => sum + (i.VatAmount ?? 0), 0);
            const advance = Number(header.AdvanceAmount || 0);
            const balance = (totalAmount - advance).toFixed(3);

            // ===== ITEMS TABLE =====
            const itemRows = items.map((item, idx) => {
                if (!item.ParticularsName) {
                    return ["", "", "", "", "", ""];
                }
                return [
                    (idx + 1).toString(),
                    `${item.ParticularsName}${item.Remarks ? ` ( ${item.Remarks} )` : ''}`,
                    (item.Amount ?? 0).toFixed(3),
                    (item.VatRate ?? 0).toFixed(3),
                    (item.VatAmount ?? 0).toFixed(3),
                    (item.TotalAmount ?? 0).toFixed(3)
                ];
            });

            const sumAmount = items.reduce((sum, i) => sum + (i.Amount ?? 0), 0);
            const sumVat = items.reduce((sum, i) => sum + (i.VatAmount ?? 0), 0);
            const sumTotal = items.reduce((sum, i) => sum + (i.TotalAmount ?? 0), 0);

            autoTable(doc, {
                startY: y,
                head: [['No', 'Particulars', 'Amount (RO)', 'VAT %', 'VAT Amount (RO)', 'Total Amount (RO)']],
                body: [
                    ...itemRows,
                    ['', 'TOTAL', sumAmount.toFixed(3), '', sumVat.toFixed(3), sumTotal.toFixed(3)]
                ],
                styles: {
                    font: "times",           // ✅ Use Times New Roman
                    fontSize: 10,            // ✅ Font size 10
                    lineColor: [0, 0, 0],
                    textColor: [0, 0, 0],
                    lineWidth: 0.2,
                    cellPadding: 2,
                    halign: 'center'
                },
                headStyles: {
                    fillColor: [240, 240, 240],
                    textColor: [0, 0, 0],
                    font: "times",          // ✅ Ensure header also uses Times New Roman
                    fontStyle: "bold",
                    fontSize: 8,           // ✅ Consistent font size
                    lineWidth: 0.2
                },
                bodyStyles: {
                    font: "times",
                    fontSize: 9,
                    halign: 'right'
                },
                columnStyles: {
                    0: { cellWidth: 8, halign: 'center' },
                    1: { cellWidth: 102, halign: 'left' },
                    2: { cellWidth: 21, halign: 'right' },
                    3: { cellWidth: 14, halign: 'right' },
                    4: { cellWidth: 27, halign: 'right' },
                    5: { cellWidth: 28, halign: 'right' }
                },
                didParseCell: (data) => {
                    if (data.row.index === itemRows.length) {
                        // ✅ Highlight TOTAL row
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = [240, 240, 240];
                        data.cell.styles.fontSize = 10;
                    }
                },
                theme: "grid",
                margin: { left: 5, right: 5 }
            });

            // ===== TOTALS SECTION =====
            pageWidth = doc.internal.pageSize.getWidth();
            y = doc.lastAutoTable.finalY;
            const totalRowHeight = 8;

            doc.setLineWidth(0.1);



            // === Define layout ===
            const boxXx = 5;
            const boxWidthx = pageWidth - 10;
            const boxHeight = 7;

            // Keep left layout same
            const colSplitLeft = boxXx + boxWidthx * 0.655; // separation between left and right
            // Add extra column split on right side for label/value separation
            const colSplitRight = boxXx + boxWidthx * 0.86;

            // ===== Row 1: Total VAT + Advance =====
            doc.setLineWidth(0.2);
            doc.rect(boxXx, y, boxWidthx, boxHeight);

            // Draw column separators
            doc.line(colSplitLeft, y, colSplitLeft, y + boxHeight);
            doc.line(colSplitRight, y, colSplitRight, y + boxHeight);

            doc.setFont("times", "bold").setFontSize(10);

            // Left side (same as before)
            doc.text(`Total VAT Amount :  ${totalVat.toFixed(3)}`, boxXx + 2, y + 5);

            // Right side (split into label/value)
            doc.text("Advance Amount ", colSplitLeft + 3, y + 5);
            doc.text(`${advance.toFixed(3)}`, boxXx + boxWidthx - 3, y + 5, { align: "right" });
            y += boxHeight;

            // ===== Row 2: Invoice without VAT + Balance =====
            doc.rect(boxXx, y, boxWidthx, boxHeight);
            doc.line(colSplitLeft, y, colSplitLeft, y + boxHeight);
            doc.line(colSplitRight, y, colSplitRight, y + boxHeight);

            // Left side (same as before)
            doc.text(`Invoice Amount without VAT :  ${taxableAmount.toFixed(3)}`, boxXx + 2, y + 5);

            // Right side (split into label/value)
            doc.text("Balance Amount ", colSplitLeft + 3, y + 5);
            doc.text(`${balance}`, boxXx + boxWidthx - 3, y + 5, { align: "right" });
            y += boxHeight;



            // === Amount in Words + Remarks + Declaration Section ===
            const sectionTop = y;
            const boxXa = 5;
            const boxWidtha = pageWidth - 10;
            let ty = sectionTop + 7;

            // === 1️⃣ Amount in Words ===
            doc.setFont("times", "normal").setFontSize(10);
            const labelText = "Amount Chargeable in words:";
            doc.text(labelText, boxXa + 2, ty);

            doc.setFont("times", "bold");
            const amountInWords = ConvertAmountToRialBaisa(balance);
            const amountX = boxXa + 2 + doc.getTextWidth(labelText) + 3;
            doc.text(` ${amountInWords}`, amountX, ty);

            ty += 8;

            // === 2️⃣ Remarks ===
            doc.setFont("times", "bold").setFontSize(10);
            doc.text("Remarks", boxXa + 2, ty);
            ty += 6;

            doc.setFont("times", "normal").setFontSize(10);
            const remarksText = `${header.Remarks1 || ""} ${header.Remarks2 || ""}`.trim();
            if (remarksText) {
                const remarksLines = doc.splitTextToSize(remarksText, boxWidtha - 10);
                doc.text(remarksLines, boxXa + 4, ty);
                ty += remarksLines.length * 4;
            } else {
                ty += 4;
            }

            // === 3️⃣ Declaration ===
            doc.setFont("times", "bold").setFontSize(10);
            doc.text("Declaration", boxXa + 2, ty + 3);
            ty += 8;

            doc.setFont("times", "normal").setFontSize(10);
            const declarationLines = [
                "1. Insurance of cargo by client.",
                "2. All commercial documents attached with invoice to be kept on record for 5 years from the date of custom clearance, to be",
                "   submitted to DG Customs on demand."
            ];
            doc.text(declarationLines, boxXa + 4, ty);

            // calculate total height based on content
            const lineHeight = 4;
            const bottomPadding = 4;
            ty += declarationLines.length * lineHeight + bottomPadding;

            // Draw the box dynamically
            const sectionHeight = ty - sectionTop;
            doc.rect(boxXa, sectionTop, boxWidtha, sectionHeight);

            // ===== SIGNATURE SECTION =====
            y = sectionTop + sectionHeight;

            const margin = 5;
            const leftWidth = 70; // Left box width
            const rightWidth = pageWidth - 2 * margin - leftWidth;
            const topHeight = 12;
            const bottomHeight = 12;

            // === LEFT BOX ===
            doc.rect(margin, y, leftWidth, topHeight + bottomHeight);
            doc.setFont("times", "bold").setFontSize(10);
            doc.text("Customers Seal and Signature", margin + 5, y + topHeight + 5);

            // === RIGHT BOX ===
            const rightX = margin + leftWidth;
            doc.rect(rightX, y, rightWidth, topHeight); // top right

            // Set font and place text inside the box (left aligned with small padding)
            doc.setFont("times", "bold").setFontSize(10);
            doc.text("For Rashid Al Hadi Trading", rightX + 5, y + 7); // ✅ left-aligned inside column

            // === Bottom right 3-column section ===
            const bottomY = y + topHeight;
            doc.rect(rightX, bottomY, rightWidth, bottomHeight);

            const colW = rightWidth / 3;


            doc.setFont("times", "bold").setFontSize(9);
            // Move text slightly lower (was +7 → now +9)
            const labelYOffset = 10;

            doc.text("Prepared by", rightX + colW / 2, bottomY + labelYOffset, { align: "center" });
            doc.text("Verified By", rightX + colW * 1.5, bottomY + labelYOffset, { align: "center" });
            doc.text("Authorized Signatory", rightX + colW * 2.5, bottomY + labelYOffset, { align: "center" });

            // // ===== OPEN & PRINT =====
            const blob = doc.output('blob');
            const blobUrl = URL.createObjectURL(blob);
            const printWindow = window.open(blobUrl, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.focus();
                    printWindow.print();
                };
            }

        } catch (err) {
            console.error("Error generating invoice:", err);
        }
    };

    const NumberToWords = (num) => {
        const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const teens = ['Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', 'Ten', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const convert = (num) => {
            if (num === 0) return '';
            if (num < 10) return units[num];
            if (num === 10) return tens[1];
            if (num < 20) return teens[num - 11];
            if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + units[num % 10] : '');
            if (num < 1000) return units[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + convert(num % 100) : '');
            if (num < 100000) return convert(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 !== 0 ? ' ' + convert(num % 1000) : '');
            if (num < 10000000) return convert(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 !== 0 ? ' ' + convert(num % 100000) : '');
            return 'Number is too large';
        };

        if (num === 0) return 'Zero';
        if (num < 0) return 'Minus ' + convert(Math.abs(num)).trim();
        return convert(num).trim();
    };

    const ConvertAmountToRialBaisa = (amount) => {
        const numericAmount = parseFloat(amount);

        if (isNaN(numericAmount)) return 'Invalid Amount';

        const isNegative = numericAmount < 0;
        const absAmount = Math.abs(numericAmount);

        const formattedAmount = absAmount.toFixed(3); // keep 3 decimals
        const [rials, baisa] = formattedAmount.split('.');

        let words = `${NumberToWords(parseInt(rials))} Rial`;
        if (baisa && parseInt(baisa) > 0) {
            words += ` And ${NumberToWords(parseInt(baisa))} Baisa`;
        }

        return (isNegative ? 'Minus ' : '') + words + ' Only';
    };

    return (
        <div className="container mt-2 mx-auto" style={{ maxWidth: '99%' }}>
            <div className='w-full mb-2 flex'>
                <h6 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif', textTransform: 'uppercase' }}> consignment </h6>
                {loading && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}>
                        <div className="loader"></div>
                        <span style={{ color: 'white', marginTop: '10px', fontSize: '14px' }}>
                            Loading...
                        </span>
                    </div>
                )}
                <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'left', marginTop: '-10px' }}>
                    <input
                        className="shadow text-gray-700 leading-tight"
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        style={{
                            width: isFocused ? '270px' : '200px',
                            height: '27px',
                            border: '1px solid #ccc',
                            borderRadius: '15px',
                            padding: '0 20px',
                            transition: 'width 0.4s ease-in-out, border-color 0.3s ease-in-out',
                            outline: 'none',
                            borderColor: isFocused ? 'darkcyan' : '#ccc',
                            boxShadow: isFocused ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                        }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px', marginTop: '-25px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber" style={{ fontSize: '11px', height: '10px' }}>
                        From Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline h-8"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px', marginTop: '-25px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber" style={{ fontSize: '11px', height: '10px' }}>
                        To Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline h-8"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>

                <Tooltip
                    title="Get Consignment Details" arrow
                    componentsProps={{
                        tooltip: {
                            sx: {
                                backgroundColor: '#0b627cff',
                                color: 'white',
                                border: '1px solid white',
                                fontWeight: 'bold',
                                boxShadow: 2,
                            },
                        },
                    }}
                >
                    <Button
                        style={{ backgroundColor: '#0b627cff', color: "whitesmoke", height: '30px', marginLeft: '10px', marginTop: '-0.95%' }}
                        onClick={fetchTableData}
                        className="linkcolor"
                    >
                        <span>Refresh</span>
                    </Button>
                </Tooltip>

                <Tooltip
                    title="Create Consignment" arrow
                    componentsProps={{
                        tooltip: {
                            sx: {
                                backgroundColor: '#045157ff',
                                color: 'whitesmoke',
                                border: '1px solid white',
                                fontWeight: 'bold',
                                boxShadow: 2,
                            },
                        },
                    }}
                >
                    <Button
                        style={{ background: 'linear-gradient(to right, #041815ff, #045157ff)', color: "whitesmoke", height: '30px', marginLeft: '10px', marginTop: '-0.95%' }}
                        component={Link}
                        to="/InvoiceCreate"
                        className="linkcolor"
                    >
                        <span>Create</span>
                    </Button>
                </Tooltip>

            </div>

            <div className="watermark" >
                <CopyrightIcon /><b style={{ fontFamily: 'serif' }}>EFFTABS SOLUTIONS LLP</b>
            </div>

            <div className='mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    style={{
                                        background: 'linear-gradient(to right, #173B45, #173B45)', borderRadius: '5px', color: 'white', width: '2%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Sl.No
                                </TableCell>
                                <TableCell
                                    style={{
                                        background: 'linear-gradient(to right, #173B45, #173B45)', borderRadius: '5px', color: 'white', width: '3%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Date
                                </TableCell>
                                <TableCell
                                    style={{
                                        background: 'linear-gradient(to right, #173B45, #173B45)', borderRadius: '5px', color: 'white', width: '10%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Invoice No
                                </TableCell>
                                <TableCell
                                    style={{
                                        background: 'linear-gradient(to right, #173B45, #173B45)', borderRadius: '5px', color: 'white', width: '10%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    BL No
                                </TableCell>
                                <TableCell
                                    style={{
                                        background: 'linear-gradient(to right, #173B45, #173B45)', borderRadius: '5px', color: 'white', width: '20%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Customer
                                </TableCell>
                                <TableCell
                                    style={{
                                        background: 'linear-gradient(to right, #173B45, #173B45)', borderRadius: '5px', color: 'white', width: '20%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Consignee
                                </TableCell>

                                <TableCell
                                    style={{
                                        background: 'linear-gradient(to right, #173B45, #173B45)', borderRadius: '5px', color: 'white', width: '10%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Total (RO)
                                </TableCell>
                                <TableCell
                                    style={{
                                        background: 'linear-gradient(to right, #173B45, #173B45)', borderRadius: '5px', color: 'white', width: '10%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Status
                                </TableCell>
                                <TableCell
                                    style={{
                                        background: 'linear-gradient(to right, #173B45, #173B45)', borderRadius: '5px', color: 'white', width: '10%',
                                        fontWeight: '600', fontSize: '11px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', width: '10%'
                                    }}>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredData && filteredData.map((item, index) => (
                                <TableRow
                                    key={item.Id}
                                    sx={{
                                        '&:hover': { backgroundColor: '#F1E7E7' },
                                        transition: 'background-color 0.3s ease-in-out',
                                        cursor: 'pointer',
                                        backgroundColor:
                                            item.ConsignmentStatusName === "Created" ? "#FFF8DC" : // light yellow
                                                item.ConsignmentStatusName === "Pending" ? "#FFEAEA" : // light red
                                                    item.ConsignmentStatusName === "Hold" ? "#FFF3CD" :    // light orange
                                                        item.ConsignmentStatusName === "Completed" ? "#E6FFED" : // light green
                                                            "inherit"
                                    }}
                                >
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '2%', fontSize: '12px' }}>
                                        {index + 1}
                                    </TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', textAlign: 'center', width: '3%', fontSize: '12px' }}>
                                        {item.InvoiceDate ? formatDate(item.InvoiceDate) : "-"}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            padding: '2px',
                                            border: '1px solid #f8d7da',
                                            width: '11%',
                                            fontSize: '12px'
                                        }}
                                    >
                                        {`${item.BillPrefix || ''}/${item.InvoiceNumber || ''}/${item.InvoiceDate ? new Date(item.InvoiceDate).getFullYear() : ''}`}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            padding: '2px',
                                            border: '1px solid #f8d7da',
                                            width: '11%',
                                            fontSize: '12px'
                                        }}
                                    >
                                        {`${item.BLNumber || ''}`}
                                    </TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '22%', fontSize: '12px' }}>{item.CustomerName}</TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '22%', fontSize: '12px' }}>{item.ConsigneeName}</TableCell>

                                    <TableCell sx={{ padding: '2px', textAlign: 'right', border: '1px solid #f8d7da', width: '10%', fontSize: '12px' }}>
                                        {item.TotalAmount ? item.TotalAmount.toFixed(3) : "0.00"}
                                    </TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '10%', fontSize: '12px' }}>
                                        {item.ConsignmentStatusName}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            padding: '2px',
                                            border: '1px solid #f8d7da',
                                            width: '10%',
                                            fontSize: '12px'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                gap: '4px',
                                                flexWrap: 'nowrap',
                                            }}
                                        >
                                            {/* Edit Invoice */}
                                            {/* {item.IsEdited === false && (
                                                
                                            )} */}
                                            <Tooltip title="Edit Consignment" arrow>
                                                <IconButton
                                                    size="small"
                                                    component={Link}
                                                    to={`/InvoiceEdit/${item.Id}`}
                                                    color="primary"
                                                >
                                                    <EditIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>

                                            {/* Consignment Details */}
                                            <Tooltip title="Consignment Details" arrow>
                                                <IconButton
                                                    size="small"
                                                    component={Link}
                                                    to={`/InvoiceDetails/${item.Id}`}
                                                >
                                                    <Info sx={{ fontSize: 20, color: 'coral' }} />
                                                </IconButton>
                                            </Tooltip>

                                            {/* Consignment Print */}
                                            <Tooltip title="Consignment Print" arrow>
                                                <IconButton size="small" onClick={() => generateInvoice(item.Id)}>
                                                    <PrintIcon sx={{ fontSize: 18, color: 'darkmagenta' }} />
                                                </IconButton>
                                                {/* onClick={() => handlePrint(item.Id)} */}
                                            </Tooltip>

                                            {/* Delete or Activate Invoice */}
                                            <Tooltip
                                                title={item.IsActive ? 'Delete Consignment' : 'Activate Consignment'}
                                                arrow
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        item.IsActive
                                                            ? openConfirmDialog(item.Id)
                                                            : handleActivate(item.Id)
                                                    }
                                                    color="error"
                                                >
                                                    {item.IsActive ? (
                                                        <DeleteIcon sx={{ fontSize: 18 }} />
                                                    ) : (
                                                        <ActivateIcon sx={{ fontSize: 18 }} />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>

                                </TableRow>
                            ))}
                        </TableBody>

                        <Dialog
                            open={confirmDialogOpen}
                            onClose={closeConfirmDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    Are you sure you want to delete this record?
                                </DialogContentText>
                            </DialogContent>

                            <DialogActions>
                                <button onClick={closeConfirmDialog} className="bg-red-600 text-white border border-red-600 rounded px-4 py-2 mr-2">
                                    Cancel
                                </button>
                                <button onClick={handleDelete} className="bg-green-600 text-white border border-green-600 rounded px-4 py-2">
                                    Confirm
                                </button>
                            </DialogActions>
                        </Dialog>
                    </Table>
                </TableContainer>
            </div >

        </div >
    );
}

export default InvoiceIndex;

